import { agentTargetId, type ThinkingLevel } from "./agentTarget"
import { DEFAULT_MODEL, HARNESS_META, type ModelEntry, type ProviderModelRegistry } from "./modelRegistry"
import { normalizeNewTaskPinnedAgents, type NewTaskAgentTarget } from "./persistence/newTaskAgentPins"
import type { HarnessId } from "./providerEvents/harnessEventTypes"
import type { ModelPricingMetadata } from "./modelCatalog"

export interface NewTaskAgentOption extends NewTaskAgentTarget {
    id: string
    harnessId: HarnessId
    harnessLabel: string
    vendorLabel: string
    modelLabel: string
    compactModelLabel: string
    default: boolean
    legacy: boolean
    latest: boolean
    tier?: string
    nativeProvider?: string
    supportsImages?: boolean
    pricing?: ModelPricingMetadata
    catalogOrder: number
}

interface PinnableAgentOption {
    thinking?: ThinkingLevel
    id: string
    harnessId: string
    modelId: string
    modelLabel: string
    compactModelLabel?: string
    default: boolean
}

function targetId(target: { harnessId: string; modelId: string }): string {
    return `${target.harnessId}:${target.modelId}`
}

function compactModelLabel(model: ModelEntry): string {
    const normalized = normalizedSearchValue(`${model.id} ${model.label}`)
    for (const family of ["astra", "sol", "luna", "fable", "opus", "sonnet"] as const) {
        if (normalized.split(" ").includes(family)) return family[0].toUpperCase() + family.slice(1)
    }
    if (model.latest && model.displayClass !== "Default") return model.displayClass
    return model.label
}

function optionFromModel(harnessId: HarnessId, model: ModelEntry, catalogOrder: number): NewTaskAgentOption {
    const harness = HARNESS_META[harnessId]
    return {
        id: targetId({ harnessId, modelId: model.id }),
        harnessId,
        modelId: model.id,
        harnessLabel: harness.name,
        vendorLabel: harness.vendor,
        modelLabel: model.label,
        compactModelLabel: compactModelLabel(model),
        default: model.default === true || model.id === DEFAULT_MODEL,
        legacy: model.legacy === true,
        latest: model.latest === true,
        ...(model.tier ? { tier: model.tier } : {}),
        ...(model.nativeProvider ? { nativeProvider: model.nativeProvider } : {}),
        ...(model.capabilities?.images ? { supportsImages: true } : {}),
        ...(model.pricing ? { pricing: model.pricing } : {}),
        catalogOrder,
    }
}

function fallbackOption(target: { harnessId: HarnessId; modelId: string }): NewTaskAgentOption {
    const harness = HARNESS_META[target.harnessId]
    const modelLabel = target.modelId === DEFAULT_MODEL ? `${harness.name} default` : target.modelId
    return {
        id: targetId(target),
        harnessId: target.harnessId,
        modelId: target.modelId,
        harnessLabel: harness.name,
        vendorLabel: harness.vendor,
        modelLabel,
        compactModelLabel: modelLabel,
        default: target.modelId === DEFAULT_MODEL,
        legacy: false,
        latest: false,
        catalogOrder: 0,
    }
}

export function getNewTaskAgentOptions(registry: ProviderModelRegistry, fallback: { harnessId: HarnessId; modelId: string }): NewTaskAgentOption[] {
    const options: NewTaskAgentOption[] = []
    let catalogOrder = 0
    for (const harnessId of registry.selectableHarnessIds) {
        for (const model of registry.getVisibleModelEntries(harnessId)) {
            options.push(optionFromModel(harnessId, model, catalogOrder))
            catalogOrder += 1
        }
    }
    return options.length > 0 ? options : [fallbackOption(fallback)]
}

function normalizedSearchValue(value: string): string {
    return value
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, " ")
        .trim()
}

function matchesDefaultPin(option: PinnableAgentOption, harnessId: HarnessId, family: "sol" | "fable" | "opus" | "sonnet" | "luna"): boolean {
    if (option.harnessId !== harnessId || option.default) return false
    const values = [option.modelId, option.modelLabel, option.compactModelLabel].map((value) => normalizedSearchValue(value ?? ""))
    return values.some((value) => value.split(" ").includes(family))
}

export function defaultNewTaskPinnedAgents<Option extends PinnableAgentOption>(options: readonly Option[]): Option[] {
    const defaults = [
        options.find((option) => matchesDefaultPin(option, "codex", "sol")),
        options.find((option) => matchesDefaultPin(option, "claude-code", "fable")),
        options.find((option) => matchesDefaultPin(option, "claude-code", "opus")),
        options.find((option) => matchesDefaultPin(option, "claude-code", "sonnet")),
        options.find((option) => matchesDefaultPin(option, "codex", "luna")),
    ]
    return defaults.filter((option): option is Option => option !== undefined)
}

export function getNewTaskPinnedAgents<Option extends PinnableAgentOption>({
    options,
    pinned,
}: {
    options: readonly Option[]
    pinned?: readonly NewTaskAgentTarget[]
}): Option[] {
    if (pinned === undefined) return defaultNewTaskPinnedAgents(options)
    const optionsById = new Map(options.map((option) => [targetId({ harnessId: option.harnessId, modelId: option.modelId }), option]))
    return normalizeNewTaskPinnedAgents(pinned)
        .map((target) => {
            const option = optionsById.get(targetId(target))
            return option && target.thinking ? { ...option, thinking: target.thinking, id: agentTargetId(target) } : option
        })
        .filter((option): option is Option => option !== undefined)
}
